import axios from "axios";
import React, { useEffect, useState } from "react";
import PageStatus from "../../PageStatus/PageStatus";
import { convertTime } from "../../../utils/timeUtils";
import { useOutlet } from "../../../context/OutletContext";

const VoidTodayKitchen = () => {
    const token = localStorage.getItem("token");
    const baseUrl = process.env.REACT_APP_BASE_URL;

    const { defaultOutlet } = useOutlet();

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!token || !defaultOutlet) return;

        const today = new Date().toLocaleDateString("en-CA");

        const fetchVoidOrders = async () => {
            try {
                setLoading(true);
                setError("");

                const response = await axios.post(
                    `${baseUrl}/void-orders-today`,
                    {
                        startDate: today,
                        endDate: today,
                        outletName: defaultOutlet,
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                setOrders(response.data?.orders || []);
            } catch (err) {
                setError(
                    err?.response?.data?.error ||
                    err?.response?.data?.message ||
                    "Failed to fetch void orders."
                );
                setOrders([]);
            } finally {
                setLoading(false);
            }
        };

        fetchVoidOrders();
    }, [defaultOutlet, token, baseUrl]);

    return (
        <div className="px-6 py-4 max-w-6xl mx-auto bg-[#FAFAF8]">

            <PageStatus
                loading={loading}
                error={error}
                loadingText="Loading void orders..."
            />

            {!loading && !error && orders.length === 0 && (
                <div className="max-w-xl mx-auto mt-10">
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-center">
                        <h2 className="text-lg font-semibold text-amber-700">
                            No Void Orders Found
                        </h2>

                        <div className="text-sm text-amber-600 mt-1">
                            There are no void orders for today.
                        </div>
                    </div>
                </div>
            )}

            {!loading && !error && orders.map((order, index) => (
                <div
                    key={index}
                    className="bg-white rounded-xl border border-[#E0DDD8] mb-6 overflow-hidden"
                >
                    {/* Header */}
                    <div className="bg-[#F8F7F3] border-b border-[#E0DDD8] px-4 py-2.5">
                        <div className="flex flex-wrap gap-2 justify-between">
                            {Number.isFinite(Number(order.TableNum)) ? (
                                <div className="flex gap-2 items-center">
                                    <div className="text-sm uppercase tracking-wide text-[#AAA69D] font-semibold">
                                        Table :
                                    </div>
                                    <div className="text-sm font-semibold text-[#2C2C2A]">
                                        {order.TableNum}
                                    </div>
                                </div>
                            ) : (
                                <div className="text-sm font-semibold text-[#2C2C2A]">
                                    Take Away
                                </div>
                            )}

                            <div className="flex gap-2 items-center">
                                <div className="text-sm uppercase tracking-wide text-[#AAA69D] font-semibold">
                                    Employee :
                                </div>
                                <div className="text-sm font-semibold text-[#2C2C2A]">
                                    {order.Employee}
                                </div>
                            </div>

                            <div className="flex gap-2 items-center">
                                <div className="text-sm uppercase tracking-wide text-[#AAA69D] font-semibold">
                                    KOT ID :
                                </div>
                                <div className="text-sm font-semibold text-[#2C2C2A]">
                                    {order.KOTID}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto tiny-scrollbar">
                        <table className="w-full text-sm text-center">
                            <thead>
                                <tr className="border-b border-[#F0EDE8] text-xs bg-white">
                                    {[
                                        "#",
                                        "Qty",
                                        "Item",
                                        "Ordered At",
                                        "Void At",
                                        "Void Time",
                                        "Status",
                                    ].map((header) => (
                                        <th
                                            key={header}
                                            className="
                                                px-3 py-2.5
                                                font-semibold
                                                text-[#8a8780]
                                                uppercase text-xs
                                                tracking-wider
                                                whitespace-nowrap
                                            "
                                        >
                                            {header}
                                        </th>
                                    ))}
                                </tr>
                            </thead>

                            <tbody>
                                {order.items.map((item, itemIndex) => (
                                    <tr
                                        key={itemIndex}
                                        className="
                                            border-b border-[#F8F7F3]
                                            hover:bg-[#FAFAF8]
                                            transition-colors
                                            duration-100
                                        "
                                    >
                                        <td className="px-2.5 py-2 whitespace-nowrap font-medium text-[#5a5854]">
                                            {itemIndex + 1}
                                        </td>

                                        <td className="px-2.5 py-2">
                                            <span className="
                                                inline-flex
                                                items-center
                                                justify-center
                                                w-7
                                                h-7
                                                rounded-full
                                                bg-[#F3F1EC]
                                                text-xs
                                                font-semibold
                                                text-[#4b4b46]
                                            ">
                                                {item.Quantity}
                                            </span>
                                        </td>

                                        <td className="px-2.5 py-2 whitespace-nowrap font-medium text-[#2C2C2A]">
                                            {item.ItemName}
                                        </td>

                                        <td className="px-2.5 py-2 whitespace-nowrap font-medium text-[#5a5854]">
                                            {convertTime(item.orderedAt)}
                                        </td>

                                        <td className="px-2.5 py-2 whitespace-nowrap font-medium text-[#5a5854]">
                                            {convertTime(item.voidAt)}
                                        </td>

                                        <td className="px-2.5 py-2 whitespace-nowrap font-medium text-[#5a5854]">
                                            {item.voidTotalTime}
                                        </td>

                                        <td className="px-2.5 py-2">
                                            <span
                                                className="inline-flex items-center text-xs gap-1.5 px-2.5 py-1 rounded-full font-semibold border"
                                                style={{
                                                    background: "#FEF2F2",
                                                    borderColor: "#FECACA",
                                                    color: "#DC2626",
                                                }}
                                            >
                                                <span className="w-1.5 h-1.5 rounded-full bg-[#DC2626]" />
                                                Void
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ))}

        </div>
    );
};

export default VoidTodayKitchen;
